import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

export interface NameValue {
  url?: boolean;
  name: string;
  value: string;
  stableCosmicId?: string | undefined
}

export function itemToNameValue(item): NameValue[] {
  const nameValueItem = [];

  Object.keys(item).forEach(key => {
    let value = item[key];
    let stable_cosmic_id = item[key].stable_cosmic_id
    if (Array.isArray(value)) {
      value = value.join(',');
    } else if (value && value.display) {
      value = value.display;
    } else if (value.CommonName) {
      value = value.CommonName;
    }

    nameValueItem.push({
      name: key,
      value: value,
      stableCosmicId:stable_cosmic_id
    });
  });

  return nameValueItem;
}

@Component({
  selector: 'key-value-card',
  templateUrl: './key-value-card.component.html',
  styleUrls: ['./key-value-card.component.scss']
})
export class KeyValueCardComponent implements OnChanges {

  constructor(
    private translate: TranslateService
  ) {

  }

  @Input() data: NameValue[];
  @Input() translationPrefix: string;
  @Input() configuration: { [key: string]: any };
  @Input() isCard: boolean;
  @Input() columns: number = 1;

  /**
   * holds a reference to a function that analyzes a given data structure and allows different
   *  extraction of the data, apart of the default name / value extraction
   */
  @Input() additionKeyValueItemsResolverRef: (keyValue: any) => NameValue[];

  displayedData: NameValue[];

  private static isUrl(str) {
    const regex = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/|ftp:\/\/)?[a-zа-я0-9]+([\-.]{1}[a-zа-я0-9]+)*\.[a-zа-я]{2,5}(:[0-9]{1,5})?(\/.*)?$/gi;

    return typeof str === 'string' ? regex.test(str) : false;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.displayedData = this.data;
    if (changes.data.currentValue !== changes.data.previousValue) {
      let currentData = this.data;

      if (this.configuration && this.configuration.values) {
        const order = this.configuration.values.map(value => value.name);
        currentData = currentData.sort((kv1, kv2) => order.indexOf(kv1.name) - order.indexOf(kv2.name));
      }

      const filteredData: NameValue[] = [];
      currentData.forEach(dataItem => {
        if (this.shouldDisplayValue(dataItem)) {
          filteredData.push(this.getKeyValue(dataItem))
          // if having a function to analyze the given dataItem:
          if (this.additionKeyValueItemsResolverRef) {
            // check if there is additional data for the given dataItem,
            //  and add it to the filteredData if exists
            const additionItems: NameValue[] = this.additionKeyValueItemsResolverRef(dataItem);

            if (additionItems) {
              additionItems.forEach(additionItem => filteredData.push(this.getKeyValue(additionItem)));
            }
          }
        }
      });

      this.displayedData = filteredData;
    }
  }

  getKey(annotationName: string) {
    const config = this.getKeyConfig(annotationName);

    return config.alias ? config.alias : annotationName;
  }

  getKeyConfig(annotationName: string) {
    if (this.configuration && this.configuration.values && this.configuration.values.filter(value => value.name === annotationName)) {
      return this.configuration.values.filter(value => value.name === annotationName)[0];
    }

    return {valueType: 'standard'};
  }

  cosmicURL(cosmicId: string, cosmicVId: string | undefined) {
    if (cosmicId.indexOf('COSF') >= 0) {
      return 'http://grch37-cancer.sanger.ac.uk/cosmic/fusion/summary?id=' + cosmicId.replace(/^COSF/, '');
    } else {
      if (cosmicId.indexOf('COSM') >= 0) {
        return 'http://grch37-cancer.sanger.ac.uk/cosmic/mutation/overview?id=' + cosmicId.replace(/^COSM/, '');
      } else
        if (cosmicId.indexOf('COSN') >= 0) {
        return 'http://grch37-cancer.sanger.ac.uk/cosmic/ncv/overview?id=' + cosmicVId.replace(/^COSV/, '');
      }
    }
  }

  cosmOrCosn(cosmicId: string){
    return cosmicId.indexOf('COSF') >= 0 || cosmicId.indexOf('COSM') >= 0;
  }

  getLinkName(keyValue) {
    switch (keyValue.name) {
      case 'gene_nbci_url':
      case 'gene_pecan_url':
      case 'gene_name1_nbci_url':
      case 'gene_name1_pecan_url':
      case 'gene_name2_nbci_url':
      case 'gene_name2_pecan_url':
        return keyValue.value.split('/').pop();
      default:
        return keyValue.value;
    }
  }

  private getTranslatedValue(value: string): string {
    if (!value) {
      return value;
    }

    const translation = this.translate.instant(`${this.translationPrefix}.${value}`, value);

    return `${this.translationPrefix}.${value}` !== translation ? translation : value;
  }

  private getKeyValue(keyValue: any): NameValue {
    let value = keyValue.value;
    let stable_cosmic_id = keyValue.stable_cosmic_id;
    const config = this.getKeyConfig(keyValue.name);
    if (config.regex) {
      const matches = new RegExp(config.regex).exec(value);

      if (matches !== null) {
        value = matches[1] ? matches[1] : matches[0];
      }
    }

    value = this.getTranslatedValue(value);

    return {
      url: KeyValueCardComponent.isUrl(value),
      name: keyValue.name,
      value: value,
      stableCosmicId: stable_cosmic_id
    };
  }

  private shouldDisplayValue(keyValue) {
    // include
    if (this.configuration && this.configuration.filterValues) {
      return this.configuration.filterValues.map(values => values.name).includes(keyValue.name);
    }

    // exclude
    if (this.configuration && this.configuration.excludeProperties) {
      return !this.configuration.excludeProperties.includes(keyValue.name);
    }

    return true;
  }
}
